import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CircularProgress, TableSortLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import Typography from "@mui/material/Typography";
import React, { useCallback, useRef, useState } from "react";
import { View } from "react-native";
import { makeStyles } from "tss-react/mui";
import { FONT_FAMILY, THEME_COLORS } from "../../themes";
import TooltipV3 from "../TooltipV3";

const lodash = require("lodash");

const StyledTableHeader = styled(TableCell)(({ theme }) => ({
  head: {
    backgroundColor: "#00000000",
    color: THEME_COLORS.black2,
  },
  body: {
    fontFamily: FONT_FAMILY.mediumV3,
    fontSize: 14,
    borderColor: THEME_COLORS.sideMenuBg,
    textTransform: "uppercase",
  },
}));

const StyledTableCell = styled(TableCell)(() => ({
  head: {
    backgroundColor: THEME_COLORS.sideMenuBg,
    color: THEME_COLORS.black2,
  },
  body: {
    fontSize: 14,
    borderColor: THEME_COLORS.sideMenuBg,
    "&:first-child": {
      borderTopLeftRadius: 5,
      borderBottomLeftRadius: 5,
    },
    "&:last-child": {
      borderTopRightRadius: 5,
      borderBottomRightRadius: 5,
    },
  },
}));

const useStyles = makeStyles()({
  table: {
    minWidth: 600,
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  tableHead: {
    fontFamily: FONT_FAMILY.semiBoldV3,
  },
  textContent: {
    display: "-webkit-box",
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    fontSize: "1em",
    wordBreak: "break-all",
  },
  tableContainer: {
    overflowY: "auto",
    "&:hover": {
      "&::-webkit-scrollbar-thumb": {
        WebkitBoxShadow: "inset 0 0 6px rgba(225,225,225,0.6)",
        backgroundColor: "rgba(128,128,128,0.6)",
      },
      "&::-webkit-scrollbar-track": {
        WebkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.3)",
        backgroundColor: "#F5F5F533",
      },
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: 8,
      WebkitBoxShadow: "none", //'inset 0 0 6px rgba(225,225,225,0.6)',
      backgroundColor: "transparent", //'rgba(0,0,0,0.6)',
    },
    "&::-webkit-scrollbar": {
      width: 8,
      height: 8,
    },
    "&::-webkit-scrollbar-track": {
      WebkitBoxShadow: "none", //'inset 0 0 6px rgba(0,0,0,0.3)',
      borderRadius: 10,
      backgroundColor: "transparent", // '#F5F5F533',
    },
  },
});

interface TableV3Props {
  columnData: {
    name: string;
    label: string;
    sortable?: boolean;
    component: (row: any) => JSX.Element;
    align?: "left" | "right" | "center";
    width: string | number;
  }[];
  page?: number;
  totalPages?: number;
  loadMore?: any;
  loading?: boolean;
  height?: string | number;
  data: any[];
  containerStyle?: any;
  headerStyle?: any;
  topLoading?: boolean;
  savedOrder?: string;
}

function TableV3({
  columnData,
  page = 1,
  totalPages,
  loadMore,
  loading,
  data = [],
  height,
  containerStyle = {},
  headerStyle = {},
  topLoading = false,
  savedOrder,
}: TableV3Props) {
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState(savedOrder ? savedOrder : "asc");
  const [distanceBottom, setDistanceBottom] = useState(0);
  const scrollTop = useRef(0);
  const { classes } = useStyles();

  const onSort = (orderId) => {
    setOrderBy(orderId);
    setOrder(order === "desc" ? "asc" : "desc");
  };

  const scrollListener = useCallback(
    (ev) => {
      if (!loadMore) return;
      const {
        nativeEvent: { target },
      } = ev;
      let bottom = target.scrollHeight - target.clientHeight;
      const isDownDirection = scrollTop.current < ev.nativeEvent.target.scrollTop;
      scrollTop.current = ev.nativeEvent.target.scrollTop;

      // if you want to change distanceBottom every time new data is loaded
      // don't use the if statement
      if (!distanceBottom) {
        // list height thresold percentage of executing function = 50%
        setDistanceBottom(Math.round((bottom / 100) * 50));
      }

      if (isDownDirection && target.scrollTop > bottom - distanceBottom && page < totalPages && !loading) {
        loadMore();
      }
    },
    [page, totalPages, loadMore, loading, distanceBottom]
  );

  return (
    <TableContainer
      onScroll={scrollListener}
      style={{ maxHeight: height ?? "calc(100vh - 120px)", ...containerStyle }}
      className={classes.tableContainer}
    >
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        {columnData.map((entity, index) => (
          <TooltipV3
            style={entity?.width ? { width: entity?.width, height: 60 } : { flex: 1 }}
            title={entity.headerTooltipText ?? entity.name}
          >
            <StyledTableHeader
              style={headerStyle ? { ...headerStyle, width: "100%" } : { width: "100%" }}
              className={classes.tableHead}
              align={entity.align || "left"}
              key={entity.name}
              sortDirection={"asc"}
            >
              {entity.sortable ? (
                <TableSortLabel
                  active={orderBy === entity.label}
                  direction={orderBy === entity.label ? order : "asc"}
                  onClick={() => {
                    let tempOrderBy = orderBy !== "" ? orderBy : entity.label;
                    if (entity.onSort)
                      entity.onSort(tempOrderBy === entity.label ? (order === "desc" ? "asc" : "desc") : "asc");
                    onSort(entity.label);
                  }}
                  IconComponent={() => {
                    return order === "desc" ? (
                      <ExpandLessIcon color="disabled" fontSize="medium" />
                    ) : (
                      <ExpandMoreIcon color="disabled" fontSize="medium" />
                    );
                  }}
                >
                  <div
                    style={{
                      display: "block",
                      width: "100%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {entity.name}
                  </div>
                  {orderBy === entity.label ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc" ? "sorted descending" : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              ) : (
                <div
                  style={{
                    display: "block",
                    width: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontFamily: FONT_FAMILY.semiBoldV3,
                  }}
                >
                  {entity.name}
                </div>
              )}
            </StyledTableHeader>
          </TooltipV3>
        ))}
      </View>
      {topLoading ? (
        <div
          style={{
            padding: "2em 0em",
            display: "flex",
            flex: 1,
            justifyContent: "center",
            width: "100%",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        data.map((row, idx) => (
          <View
            key={row.avgTat}
            style={{
              flexDirection: "row",
              backgroundColor: idx % 2 == 0 ? THEME_COLORS.purple3 : "#00000000",
            }}
          >
            {columnData.map((column, index) => {
              const CellComponent = typeof column?.component == "function" ? column?.component : undefined;

              return (
                <View style={column?.width ? { width: column?.width ?? "auto" } : { flex: 1 }}>
                  <StyledTableCell
                    key={column.label + index}
                    align={column?.align ?? (index === 0 ? "left" : columnData.length == 2 ? "right" : "left")}
                    component="th"
                    scope="row"
                    style={{ flex: 1 }}
                  >
                    {CellComponent ? (
                      <CellComponent row={row} />
                    ) : (
                      <TooltipV3 title={lodash.get(row, column.label) || "Not available"}>
                        <Typography className={classes.textContent} variant="body1">
                          {lodash.get(row, column.label) ?? "Not available"}
                        </Typography>
                      </TooltipV3>
                    )}
                  </StyledTableCell>
                </View>
              );
            })}
          </View>
        ))
      )}
      {!data.length && !loading && !topLoading && (
        <div
          style={{
            padding: "2em 0em",
            display: "flex",
            flex: 1,
            justifyContent: "center",
            width: "100%",
            fontFamily: FONT_FAMILY.semiBoldV3,
          }}
        >
          No data found
        </div>
      )}
      {loading && (
        <div
          style={{
            padding: "2em 0em",
            display: "flex",
            flex: 1,
            justifyContent: "center",
            width: "100%",
          }}
        >
          <CircularProgress />
        </div>
      )}
    </TableContainer>
  );
}

export { TableV3 };
